import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, BookOpen, CheckCircle2, Sparkles, Star, FileText, Loader2 } from "lucide-react";

export default function StudentPerformance() {
  const { childId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/parent/students/${childId}/performance`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [childId]);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" style={{ background: "#FFF7ED" }}>
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#13A699" }} />
      </main>
    );
  }
  if (error || !data) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" style={{ background: "#FFF7ED" }} data-testid="student-perf-error">
        <p style={{ color: "#13A699" }}>{error || "Not found"}</p>
      </main>
    );
  }
  const { child, ongoing, completed, skills, writeups } = data;

  return (
    <main className="min-h-[80vh] py-8 sm:py-12 px-4 sm:px-6" style={{ background: "#FFF7ED" }} data-testid="student-performance-page">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/parent/dashboard"
          className="inline-flex items-center gap-1 text-sm font-bold hover:underline mb-5"
          style={{ color: "#13A699" }}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        {/* Header */}
        <div className="bg-white rounded-3xl p-5 border border-black/5 flex items-center gap-4 shadow-sm">
          <div
            className="h-16 w-16 rounded-full flex items-center justify-center font-display font-bold text-2xl"
            style={{ background: "#FFD708", color: "#13A699" }}
          >
            {(child?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "#13A699" }} data-testid="student-name">
              {child?.name}
            </h1>
            <p className="text-sm" style={{ color: "#13A69988" }}>
              {[child?.grade, child?.school].filter(Boolean).join(" · ") || "Learning journey"}
            </p>
          </div>
        </div>

        {/* Top stats */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard icon={<BookOpen className="h-5 w-5" />} label="Ongoing Courses" value={ongoing.length} testId="stat-ongoing" />
          <StatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Completed Courses" value={completed.length} testId="stat-completed" />
          <StatCard icon={<Sparkles className="h-5 w-5" />} label="Skills Developed" value={skills.length} testId="stat-skills" />
        </div>

        {/* Skills */}
        <div className="mt-5 bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="skills-section">
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <Sparkles className="h-4 w-4" /> Skills Developed in the Journey
          </h2>
          {skills.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "#13A69988" }}>No skills logged yet. Skills come from mentor write-ups + course subjects when courses complete.</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="skills-list">
              {skills.map((s) => (
                <span
                  key={s}
                  className="text-xs font-bold px-3 py-1.5 rounded-full border-2"
                  style={{ borderColor: "#13A699", color: "#13A699", background: "#FFF7ED" }}
                  data-testid={`skill-tag-${s.replace(/\s+/g, "-")}`}
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Ongoing */}
        <div className="mt-5 bg-white rounded-3xl p-6 border border-black/5 shadow-sm">
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <BookOpen className="h-4 w-4" /> Ongoing Courses
          </h2>
          {ongoing.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "#13A69988" }}>No ongoing courses.</p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="ongoing-list">
              {ongoing.map((c) => (
                <li key={c.id} className="rounded-2xl border border-black/5 p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`ongoing-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-display font-bold" style={{ color: "#13A699" }}>{c.course_title}</div>
                    <div className="text-xs" style={{ color: "#13A69988" }}>
                      with {c.teacher_name} · {c.num_sessions} sessions · {c.course_subject || "—"}                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Completed */}
        <div className="mt-5 bg-white rounded-3xl p-6 border border-black/5 shadow-sm">
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <CheckCircle2 className="h-4 w-4" /> Completed Courses
          </h2>
          {completed.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "#13A69988" }}>No completed courses yet.</p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="completed-list">
              {completed.map((c) => (
                <li key={c.id} className="rounded-2xl border border-black/5 p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`completed-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-display font-bold" style={{ color: "#13A699" }}>{c.course_title}</div>
                    <div className="text-xs" style={{ color: "#13A69988" }}>
                      with {c.teacher_name} · {c.num_sessions} sessions
                    </div>
                  </div>
                  {c.rating && (
                    <span className="text-sm font-bold inline-flex items-center gap-1" style={{ color: "#13A699" }}>
                      <Star className="h-4 w-4" fill="#FFD708" stroke="#13A699" /> {c.rating}/5
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Mentor writeups */}
        <div className="mt-5 bg-white rounded-3xl p-6 border border-black/5 shadow-sm">
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <FileText className="h-4 w-4" /> Mentor Write-ups
          </h2>
          {writeups.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "#13A69988" }}>No write-ups yet. They appear here once a mentor punches in to complete a course.</p>
          ) : (
            <ul className="mt-3 space-y-3" data-testid="writeups-list">
              {writeups.map((w, idx) => (
                <li key={idx} className="rounded-2xl border border-black/5 p-3" data-testid={`writeup-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>
                      {w.course_title}
                    </span>
                    <span className="text-xs" style={{ color: "#13A69988" }}>by {w.teacher_name}</span>
                  </div>
                  <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "#13A699" }}>{w.writeup}</p>
                  {(w.skill_tags || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {w.skill_tags.map((t) => (
                        <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#13A69922", color: "#13A699" }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-black/5 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2" style={{ color: "#13A699" }}>
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
      </div>
      <div className="mt-1 font-display text-2xl font-bold" style={{ color: "#13A699" }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Active", bg: "#FFD708", color: "#13A699" },
    teacher_completed: { label: "Awaiting your punch-in", bg: "#FBA31222", color: "#FBA312" },
    completed: { label: "Completed", bg: "#13A69922", color: "#13A699" },
  };
  const s = map[status] || map.active;
  return (
    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}
